/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { useSyncExternalStore } from 'react';
import type {
  AgentWorkspace,
  AgentWorkspaceItemLayout,
  AgentWorkspaceWidget,
  AgentWorkspaceWidgetSource,
  ExpandedAgentArtifact,
} from './types';
import { getArtifactWidgetId, placeWidget } from './workspaceLayout';

const EMPTY_WORKSPACE: AgentWorkspace = { widgets: [], layout: [] };
export const WORKSPACE_STORAGE_PREFIX = 'agent-workspace:';
const STORAGE_PREFIX = WORKSPACE_STORAGE_PREFIX;

const workspaces = new Map<string, AgentWorkspace>();
const listeners = new Set<() => void>();
// Conversations with local edits the server hasn't saved yet, and who wants to hear about them.
const dirty = new Set<string>();
// Bumped by `reset` (logout, account change): async work started before it must not write back.
let resetEpoch = 0;
const changeListeners = new Set<(conversationId: string) => void>();

// Tables hold rendered content, so only the other kinds survive a reload of this tab's cache;
// the server copy (transcript references) brings tables back too.
const isSerializable = ({ artifact }: AgentWorkspaceWidget) => artifact.kind !== 'table';

const restore = (conversationId: string): AgentWorkspace => {
  try {
    const raw = sessionStorage.getItem(STORAGE_PREFIX + conversationId);
    if (!raw) return EMPTY_WORKSPACE;
    const { pending, ...parsed } = JSON.parse(raw) as AgentWorkspace & { pending?: boolean };
    if (!Array.isArray(parsed.widgets) || !Array.isArray(parsed.layout)) return EMPTY_WORKSPACE;
    // Edits the tab reloaded before saving are still unsaved: autosave picks them up again.
    if (pending) dirty.add(conversationId);
    return parsed;
  } catch {
    return EMPTY_WORKSPACE;
  }
};

const persist = (conversationId: string, workspace: AgentWorkspace) => {
  const widgets = workspace.widgets.filter(isSerializable);
  const ids = new Set(widgets.map(({ id }) => id));
  try {
    // The title alone is worth keeping: it names the window tab before the server copy loads.
    if (widgets.length === 0 && !workspace.title && !workspace.revision) {
      sessionStorage.removeItem(STORAGE_PREFIX + conversationId);
      return;
    }
    sessionStorage.setItem(
      STORAGE_PREFIX + conversationId,
      JSON.stringify({
        title: workspace.title,
        widgets,
        layout: workspace.layout.filter(({ i }) => ids.has(i)),
        revision: workspace.revision,
        syncedWidgetIds: workspace.syncedWidgetIds,
        syncedLayout: workspace.syncedLayout,
        pending: dirty.has(conversationId) || undefined,
      }),
    );
  } catch {
    // Storage full or unavailable: the workspace still works for this session.
  }
};

const getWorkspace = (conversationId: string | undefined): AgentWorkspace => {
  if (!conversationId) return EMPTY_WORKSPACE;
  let workspace = workspaces.get(conversationId);
  if (!workspace) {
    workspace = restore(conversationId);
    workspaces.set(conversationId, workspace);
  }
  return workspace;
};

const emit = () => {
  for (const listener of listeners) listener();
};

const set = (conversationId: string, next: AgentWorkspace) => {
  workspaces.set(conversationId, next);
  persist(conversationId, next);
  emit();
};

/** A user edit: stored locally right away and queued for the server. */
const update = (conversationId: string, next: AgentWorkspace) => {
  // Marked before storing, so the tab cache records the edit as unsaved too.
  dirty.add(conversationId);
  set(conversationId, next);
  for (const listener of changeListeners) listener(conversationId);
};

const sameLayoutItem = (a: AgentWorkspaceItemLayout, b: AgentWorkspaceItemLayout) =>
  a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;

// Tables hold rendered content; their parsed source identifies them instead.
const widgetContent = ({ artifact, source }: AgentWorkspaceWidget) =>
  JSON.stringify([source, artifact.kind === 'table' ? artifact.value.source : artifact.value]);

/** Whether the local workspace already has every server widget, with the same content. */
const hasServerWidgets = (local: AgentWorkspace, server: AgentWorkspace) => {
  const localById = new Map(local.widgets.map((widget) => [widget.id, widget]));
  return server.widgets.every((widget) => {
    const localWidget = localById.get(widget.id);
    return localWidget !== undefined && widgetContent(localWidget) === widgetContent(widget);
  });
};

/** What `POST .../workspace` stores: widgets that point into the transcript, and their layout. */
export const toWorkspacePayload = (workspace: AgentWorkspace) => {
  const widgets = workspace.widgets.flatMap(({ id, artifact, source }) =>
    source ? [{ id, kind: artifact.kind, source }] : [],
  );
  const ids = new Set(widgets.map(({ id }) => id));
  return {
    title: workspace.title ?? null,
    widgets,
    layout: workspace.layout.filter(({ i }) => ids.has(i)),
    revision: workspace.revision ?? 0,
  };
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

/** Per-conversation workspace: the artifacts expanded out of the chat and their grid layout. */
export const agentWorkspaceStore = {
  get: getWorkspace,
  subscribe,
  /** Adds the artifact (or finds it, when already there) and returns its widget id. */
  add(
    conversationId: string,
    artifact: ExpandedAgentArtifact,
    title?: string,
    source?: AgentWorkspaceWidgetSource,
  ) {
    const workspace = getWorkspace(conversationId);
    const id = getArtifactWidgetId(artifact);
    const existing = workspace.widgets.find((widget) => widget.id === id);
    if (existing) {
      const addsSource = source && !existing.source;
      if (addsSource || (title && title !== workspace.title)) {
        update(conversationId, {
          ...workspace,
          title: title ?? workspace.title,
          widgets: workspace.widgets.map((widget) =>
            widget === existing && addsSource ? { ...widget, source } : widget,
          ),
        });
      }
      return id;
    }
    update(conversationId, {
      ...workspace,
      title: title ?? workspace.title,
      widgets: [...workspace.widgets, { id, artifact, source }],
      layout: [...workspace.layout, placeWidget(workspace.layout, id, artifact.kind)],
    });
    return id;
  },
  remove(conversationId: string, widgetId: string) {
    const workspace = getWorkspace(conversationId);
    update(conversationId, {
      ...workspace,
      widgets: workspace.widgets.filter(({ id }) => id !== widgetId),
      layout: workspace.layout.filter(({ i }) => i !== widgetId),
    });
  },
  setLayout(conversationId: string, layout: ReadonlyArray<AgentWorkspaceItemLayout>) {
    const workspace = getWorkspace(conversationId);
    const next = layout.map(({ i, x, y, w, h }) => ({ i, x, y, w, h }));
    // The grid reports its layout on every mount; only real moves are stored.
    if (JSON.stringify(next) === JSON.stringify(workspace.layout)) return;
    update(conversationId, { ...workspace, layout: next });
  },
  /** Calls `listener` with the conversation id after every user edit. */
  subscribeChanges(listener: (conversationId: string) => void) {
    changeListeners.add(listener);
    return () => {
      changeListeners.delete(listener);
    };
  },
  isDirty: (conversationId: string) => dirty.has(conversationId),
  /** Conversations whose cached workspace holds edits saved neither before the last reload nor since. */
  pendingConversationIds() {
    return Object.keys(sessionStorage)
      .filter((key) => key.startsWith(STORAGE_PREFIX))
      .map((key) => key.slice(STORAGE_PREFIX.length))
      .filter((conversationId) => {
        getWorkspace(conversationId);
        return dirty.has(conversationId);
      });
  },
  /**
   * The server accepted `saved` as `revision`. Edits made while the request was in flight stay
   * queued; they save on top of the new revision.
   */
  markSaved(conversationId: string, saved: AgentWorkspace, revision: number) {
    const current = getWorkspace(conversationId);
    if (current === saved) dirty.delete(conversationId);
    const payload = toWorkspacePayload(saved);
    set(conversationId, {
      ...current,
      revision,
      syncedWidgetIds: payload.widgets.map(({ id }) => id),
      syncedLayout: payload.layout,
    });
  },
  /**
   * Applies the server copy. With unsaved local edits, a newer server copy is merged three-way
   * against the last synced state, so each side's additions, removals, moves and resizes survive
   * and the merge saves on top of it; otherwise it replaces the local state, keeping widgets the
   * server can't hold (no source).
   */
  applyServer(conversationId: string, server: AgentWorkspace & { revision: number }) {
    const local = getWorkspace(conversationId);
    const serverIds = new Set(server.widgets.map(({ id }) => id));
    const syncedWidgetIds = [...serverIds];
    const syncedLayout = server.layout;
    if (dirty.has(conversationId)) {
      if (server.revision <= (local.revision ?? 0)) return;
      const base = new Set(local.syncedWidgetIds);
      const localIds = new Set(local.widgets.map(({ id }) => id));
      // In the base but gone from either side: removed there, so removed here too.
      // Content isn't edited locally, so a widget both sides still have takes the server's rebuilt
      // copy (this tab may hold it from mid-stream); only this tab's additions stay local.
      const serverById = new Map(server.widgets.map((widget) => [widget.id, widget]));
      const kept = local.widgets
        .filter(({ id }) => !base.has(id) || serverIds.has(id))
        .map((widget) => (base.has(widget.id) && serverById.get(widget.id)) || widget);
      const keptIds = new Set(kept.map(({ id }) => id));
      const added = server.widgets.filter(({ id }) => !localIds.has(id) && !base.has(id));
      // A widget this tab didn't move or resize takes the server's position (another tab may have).
      const baseLayout = new Map((local.syncedLayout ?? []).map((item) => [item.i, item]));
      const serverLayout = new Map(server.layout.map((item) => [item.i, item]));
      let layout = local.layout
        .filter(({ i }) => keptIds.has(i))
        .map((item) => {
          const base = baseLayout.get(item.i);
          return base && sameLayoutItem(item, base) ? (serverLayout.get(item.i) ?? item) : item;
        });
      for (const widget of added) {
        const saved = server.layout.find(({ i }) => i === widget.id);
        layout = [...layout, saved ?? placeWidget(layout, widget.id, widget.artifact.kind)];
      }
      set(conversationId, {
        title: local.title ?? server.title,
        widgets: [...kept, ...added],
        layout,
        revision: server.revision,
        syncedWidgetIds,
        syncedLayout,
      });
      return;
    }
    // Same revision with the same widgets and content: already up to date. Content counts too,
    // since this tab's cache may hold an artifact expanded while it was still streaming.
    if (local.revision === server.revision && hasServerWidgets(local, server)) return;
    const localOnly = local.widgets.filter(({ id, source }) => !source && !serverIds.has(id));
    const localOnlyIds = new Set(localOnly.map(({ id }) => id));
    set(conversationId, {
      title: server.title ?? local.title,
      widgets: [...server.widgets, ...localOnly],
      layout: [...server.layout, ...local.layout.filter(({ i }) => localOnlyIds.has(i))],
      revision: server.revision,
      syncedWidgetIds,
      syncedLayout,
    });
  },
  /** Forgets every workspace held in memory (logout, account change, tests). */
  reset() {
    workspaces.clear();
    dirty.clear();
    resetEpoch += 1;
    emit();
  },
  epoch: () => resetEpoch,
};

export const useAgentWorkspace = (conversationId: string | undefined) =>
  useSyncExternalStore(subscribe, () => getWorkspace(conversationId));
